// AI filters management — the mute/notify filters created from emails ("AI
// filter" in the reading pane) plus category-wide ones. See, toggle, edit and
// delete them. The generic workflow rules (tag/assign/archive) stay in Rules.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, Loader2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { MuteRuleDialog, type EditableAiFilter } from '@/components/MuteRuleDialog';
import { useMe } from '@/lib/auth';

interface RuleRow {
  id: number;
  name: string;
  enabled: boolean;
  conditions: { all?: { field: string; op: string; value: string; ai?: boolean }[]; any?: { field: string; op: string; value: string; ai?: boolean }[] };
  actions: { type: string }[];
  scopeUserId: number | null;
  createdByUserId: number | null;
  hitCount: number;
  lastHitAt: string | null;
}

function describeFilter(r: RuleRow): string {
  const leaves = r.conditions.all ?? r.conditions.any ?? [];
  const parts: string[] = [];
  for (const l of leaves) {
    if (l.field === 'category') parts.push(`every "${l.value.replace('_', ' ')}" email (any sender)`);
    else if (l.field === 'from' && l.ai) parts.push(`sender the AI judges as "${l.value}"`);
    else if (l.field === 'from') parts.push(`sender contains "${l.value}"`);
    else if (l.field === 'amount') parts.push(`amount ${l.op === 'lte' ? '≤' : l.op === 'gte' ? '≥' : '='} ${l.value}${'currency' in l && (l as { currency?: string }).currency ? ` ${(l as { currency?: string }).currency}` : ''}`);
    else if (l.field === 'text') parts.push(`text contains "${l.value}"`);
  }
  const action = r.actions.some((a) => a.type === 'notify') ? 'Always notify' : 'Mute';
  return `${action} when ${parts.join(' and ') || '(no conditions)'}`;
}

export function AiFiltersSettings() {
  const qc = useQueryClient();
  const me = useMe();
  const [editing, setEditing] = useState<EditableAiFilter | null>(null);
  const q = useQuery<{ rules: RuleRow[] }>({
    queryKey: ['rules'],
    queryFn: async () => {
      const r = await fetch('/api/rules', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  // Only notification filters here — rules whose actions include mute/notify.
  const filters = (q.data?.rules ?? []).filter((r) =>
    r.actions.some((a) => a.type === 'mute' || a.type === 'notify'),
  );

  async function toggle(rule: RuleRow, enabled: boolean) {
    const r = await fetch(`/api/rules/mine/${rule.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    if (r.ok) {
      void qc.invalidateQueries({ queryKey: ['rules'] });
    } else {
      toast.error('Only the person who created a filter can change it');
    }
  }

  async function remove(rule: RuleRow) {
    if (!confirm(`Delete AI filter "${rule.name}"?`)) return;
    const r = await fetch(`/api/rules/mine/${rule.id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) {
      toast.success('Filter deleted');
      void qc.invalidateQueries({ queryKey: ['rules'] });
    } else {
      toast.error('Only the person who created a filter can delete it');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <BellOff className="size-3.5" strokeWidth={2} />
          AI filters
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Notification filters created from emails (open an email → ⋯ → AI filter). Muting only
          skips the push — mail still lands in the inbox. "Just you" filters affect only their
          creator's notifications.
        </p>

        {q.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}
        {!q.isLoading && filters.length === 0 && (
          <p className="py-4 text-sm text-muted-foreground">
            No AI filters yet. Open any email → ⋯ menu → "AI filter" to create one — e.g. mute all
            policy/terms-update emails.
          </p>
        )}
        {filters.map((r) => {
          const mine = r.createdByUserId === me.data?.user.id;
          const isNotify = r.actions.some((a) => a.type === 'notify');
          return (
            <div key={r.id} className="flex items-start gap-3 rounded-md border p-3">
              {isNotify ? (
                <BellRing className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75} />
              ) : (
                <BellOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {describeFilter(r)}
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px]">
                    {r.scopeUserId != null ? 'just its creator' : 'everyone'}
                  </span>
                  {r.hitCount > 0 && (
                    <span
                      className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px]"
                      title={r.lastHitAt ? `last: ${new Date(r.lastHitAt).toLocaleString()}` : undefined}
                    >
                      fired {r.hitCount}×
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch
                  checked={r.enabled}
                  onCheckedChange={(v) => void toggle(r, !!v)}
                  aria-label={`Toggle ${r.name}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() =>
                    setEditing({
                      id: r.id,
                      name: r.name,
                      conditions: r.conditions,
                      actions: r.actions,
                      scopeUserId: r.scopeUserId,
                    })
                  }
                  aria-label={`Edit ${r.name}`}
                  disabled={!mine}
                  title={mine ? 'Edit filter' : 'Only the creator can edit this filter'}
                >
                  <Pencil className="size-3.5" strokeWidth={1.75} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-muted-foreground hover:text-destructive"
                  onClick={() => void remove(r)}
                  aria-label={`Delete ${r.name}`}
                >
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                </Button>
              </div>
            </div>
          );
        })}

        <MuteRuleDialog
          open={editing !== null}
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
          threadId={null}
          messageId={null}
          senderDomain=""
          defaultFrom=""
          editRule={editing}
        />
      </CardContent>
    </Card>
  );
}
